<template>
  <div class="page-job section">
    <div class="container">
      <div class="columns is-centered">
        <div class="column is-10-tablet is-9-desktop is-8-widescreen">

          <template v-if="job && jobDownloaded">
            <div class="box job-header">
              <div>
                <p class="heading mb-1">Job Details</p>
                <h1 class="title is-3 mb-0">
                  Job {{ job.generated_job_id }}
                </h1>
              </div>

              <div class="job-actions">

                <button
                  class="button is-primary"
                  type="button"
                  @click="refresh"
                >
                  <span class="icon">
                    <i class="fas fa-sync"></i>
                  </span>

                  <span>Refresh</span>
                </button>

                <button
                  class="button is-link"
                  type="button"
                  @click="printPDF"
                >
                  <span class="icon">
                    <i class="fas fa-file-pdf"></i>
                  </span>

                  <span>Print PDF</span>
                </button>

              </div>
            </div>

            <div class="card job-card">
              <div class="card-content">
                <div class="job-row">
                  <span class="job-label">Job ID</span>
                  <span class="job-value">{{ job.generated_job_id }}</span>
                </div>

                <div class="job-row">
                  <span class="job-label">Address</span>
                  <span class="job-value">{{ job.job_address }}</span>
                </div>

                <div class="job-row">
                  <span class="job-label">Work Done Description</span>
                  <span class="job-value description">
                    {{ job.work_done_description || 'No description added' }}
                  </span>
                </div>

                <div class="job-row">
                  <span class="job-label">Status</span>
                  <span class="tag is-info is-light">
                    {{ job.status }}
                  </span>
                </div>
              </div>
            </div>

            <div
              v-if="photos.length > 0 && !downloadLoading"
              class="box photos-section"
            >
              <div class="photos-header">
                <div>
                  <h2 class="title is-4 mb-1">Photos</h2>
                  <p class="subtitle is-6 has-text-grey mb-0">
                    {{ photos.length }} photo{{ photos.length === 1 ? '' : 's' }} available
                  </p>
                </div>

                <label class="checkbox select-all">
                  <strong>Select All</strong>
                  <input
                    type="checkbox"
                    v-model="selectAll"
                    @change="updateSelectAll"
                  >
                </label>
              </div>

              <div class="photo-grid">
                <div
                  v-for="(photo, index) in photos"
                  :key="photo.id"
                  class="photo-card"
                >
                  <div class="photo-card-header">
                    <span class="tag is-dark is-light">
                      Photo {{ index + 1 }}
                    </span>

                    <label class="checkbox">
                      <input
                        type="checkbox"
                        v-model="photo.selected"
                        @change="updatePhoto(photo)"
                      >
                    </label>
                  </div>

                  <div
                    class="photo-image-wrapper"
                    @click="openImageModal(photo.get_image)"
                  >
                    <figure class="image photo-image">
                      <img :src="photo.get_image" alt="Job photo">
                    </figure>

                    <div class="expand-icon">
                      <span class="icon">
                        <i class="fas fa-expand"></i>
                      </span>
                    </div>
                  </div>

                  <div class="field mt-4">
                    <label class="label is-small">
                      Photo caption
                      <span class="has-text-grey has-text-weight-normal">
                        optional
                      </span>
                    </label>

                    <div class="control">
                      <textarea
                        class="textarea"
                        rows="3"
                        v-model="photo.caption"
                        @change="updatePhoto(photo)"
                        placeholder="Add a caption..."
                      ></textarea>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="job.photos_downloaded && photos.length === 0"
              class="notification is-light no-photos"
            >
              No photos to download.
            </div>

            <div v-if="!job.photos_downloaded || downloadLoading">
              <div class="box loading-box has-text-centered">
                <h2 class="title is-5 mb-3">Retrieving photos</h2>
                <p class="has-text-grey mb-4">
                  Please wait while the job photos are downloaded.
                </p>
                <div class="lds-dual-ring"></div>
              </div>
            </div>
          </template>

          <div
            v-if="!job && jobDownloaded"
            class="notification is-warning is-light has-text-centered"
          >
            No job found.
          </div>

        </div>
      </div>
    </div>
  </div>
  <div class="modal" :class="{ 'is-active': selectedImage }">
    <div class="modal-background" @click="closeImageModal"></div>

    <div class="modal-content image-modal-content">
      <p class="image">
        <img :src="selectedImage" alt="Full size job photo">
      </p>
    </div>

    <button
      class="modal-close is-large"
      aria-label="close"
      @click="closeImageModal"
    ></button>
  </div>
</template>

<script>
import axios from 'axios'
import { toast } from 'bulma-toast'

export default {
  name: 'JobView',

  data() {
    return {
      job: null,
      photos: [],
      refreshClicked: false,
      jobDownloaded: false,
      downloadLoading: false,
      selectAll: true,
      selectedImage: null,
    }
  },

  mounted() {
    document.title = 'Job | SCE Electrical'
    this.getJob()
  },

  methods: {
    showToast(message, type = 'is-danger') {
      toast({
        message,
        type,
        dismissible: true,
        pauseOnHover: true,
        duration: 2000,
        position: 'bottom-right',
      })
    },

    refresh() {
      this.jobDownloaded = false
      this.job = null
      this.photos = []
      this.refreshClicked = true
      this.getJob()
    },

    checkSelectAll() {
      if (!this.photos.length) {
        this.selectAll = false
        return
      }

      this.selectAll = this.photos.every(photo => photo.selected === true)
    },

    async getJob() {
      this.$store.commit('setIsLoading', true)

      const jobId = this.$route.params.id

      let url = `/api/v1/job/${jobId}/initial`

      if (this.refreshClicked) {
        url = `/api/v1/job/${jobId}/refresh`
      }

      try {
        const response = await axios.get(url)

        this.job = response.data
        this.photos = response.data.photos || []

        if (this.job.photos_downloaded === false || this.refreshClicked === true) {
          this.downloadPhotos()
        }

        this.checkSelectAll()
      } catch (error) {
        this.job = null
        this.photos = []

        console.log(error)

        if (error.response?.status !== 404) {
          this.showToast('Something went wrong. Please try again.')
        }
      } finally {
        this.jobDownloaded = true
        this.refreshClicked = false
        this.$store.commit('setIsLoading', false)
      }
    },

    async downloadPhotos() {
      this.downloadLoading = true

      const jobId = this.$route.params.id

      try {
        const response = await axios.get(`/api/v1/photos/${jobId}`)

        this.job.photos_downloaded = true
        this.photos = response.data.photos || []
        this.checkSelectAll()
      } catch (error) {
        console.log(error)
        this.showToast('Something went wrong. Please try again.')
      } finally {
        this.downloadLoading = false
      }
    },

    photoDate(photo) {
      return new Date(Date.parse(photo.date_added)).toString()
    },

    updatePhoto(photo) {
      axios
        .patch(`/api/v1/photo/${photo.id}/`, {
          caption: photo.caption,
          selected: photo.selected
        })
        .then(() => {
          this.checkSelectAll()
        })
        .catch(error => {
          console.log(error)
          this.showToast('Something went wrong. Please try again.')
        })
    },

    updateSelectAll() {
      for (let i = 0; i < this.photos.length; i++) {
        if (this.photos[i].selected !== this.selectAll) {
          this.photos[i].selected = this.selectAll
          this.updatePhoto(this.photos[i])
        }
      }
    },

    printPDF() {
      const openUrl = `${this.$route.params.id}/print`
      window.open(openUrl)
    },

    openImageModal(imageUrl) {
      this.selectedImage = imageUrl
    },

    closeImageModal() {
      this.selectedImage = null
    },
  }
}
</script>

<style scoped>
.page-job {
  min-height: 100%;
}

.job-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  border-radius: 12px;
}

.job-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.job-card,
.photos-section {
  border-radius: 12px;
}

.job-row {
  padding: 1rem 0;
  border-top: 1px solid #eeeeee;
}

.job-row:first-child {
  border-top: none;
  padding-top: 0;
}

.job-label {
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #7a7a7a;
  letter-spacing: 0.04em;
}

.job-value {
  display: block;
  color: #363636;
  line-height: 1.5;
}

.description {
  white-space: pre-wrap;
}

.photos-section {
  margin-top: 1.5rem;
}

.photos-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.select-all {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
}

.photo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
}

.photo-card {
  padding: 1rem;
  border: 1px solid #eeeeee;
  border-radius: 10px;
  background: #fafafa;
}

.photo-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.photo-image {
  overflow: hidden;
  border-radius: 8px;
  background: #f2f2f2;
}

.photo-image img {
  width: 100%;
  height: 180px;
  object-fit: cover;
}

.no-photos,
.loading-box {
  margin-top: 1.5rem;
}

.loading-box {
  border-radius: 12px;
}

.photo-image {
  cursor: pointer;
}

.image-modal-content {
  width: auto;
  max-width: 90vw;
}

.image-modal-content img {
  max-height: 90vh;
  object-fit: contain;
}

.photo-image-wrapper {
  position: relative;
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
}

.expand-icon {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;

  width: 34px;
  height: 34px;

  display: flex;
  align-items: center;
  justify-content: center;

  background: rgba(0, 0, 0, 0.65);
  color: white;

  border-radius: 999px;

  opacity: 0;
  transition: opacity 0.2s ease;
}

.photo-image-wrapper:hover .expand-icon {
  opacity: 1;
}

.expand-icon .icon {
  font-size: 0.9rem;
}

@media screen and (max-width: 768px) {
  .job-header,
  .photos-header {
    align-items: stretch;
    flex-direction: column;
  }

  .job-actions {
    width: 100%;
  }

  .job-actions .button {
    flex: 1;
  }

  .select-all {
    justify-content: space-between;
  }
}
</style>